import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import type {AnimationLightboxThumbnailStripProps} from './animation-lightbox-thumbnail-strip.types';
import styles from './animation-lightbox-thumbnail-strip.module.scss';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {AiChatAnimation} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';

export function AnimationLightboxThumbnailStrip({animations, currentIndex, onThumbnailClick, isCreating}: AnimationLightboxThumbnailStripProps): ReactElement {
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
  }, [currentIndex]);

  const getCreatingThumbnail = (): ReactElement => {
    const isSelected = Boolean(isCreating) && currentIndex === 0;
    const aspectRatio =
      animations[0]?.width && animations[0]?.height ? animations[0].width / animations[0].height : 1;

    return (
      <ClickableDiv
        key="creating"
        ref={isSelected ? selectedRef : undefined}
        className={`${styles.thumbnailContainer} ${isSelected ? styles.thumbnailContainerSelected : ''}`}
        style={{'--thumb-aspect-ratio': String(aspectRatio)} as React.CSSProperties}
        onClick={(): void => {
          onThumbnailClick(0);
        }}
      >
        <SkeletonBrick className={styles.thumbnailImage} theme={SkeletonBrickTheme.AI_ROTATING_GRADIENT} />
      </ClickableDiv>
    );
  };

  const getPlayIcon = (isSelected: boolean): ReactElement | null => {
    if (isSelected) {
      return null;
    }

    return (
      <div className={styles.playIcon}>
        <Icon icon={'icon-play-video'} size={IconSize.SIZE_ICON_16} shape={IconShape.CIRCLE} type={IconType.SHADOW} />
      </div>
    );
  };

  const getThumbnail = (animation: AiChatAnimation, index: number): ReactElement => {
    const offsetIndex = isCreating ? index + 1 : index;
    const isSelected = offsetIndex === currentIndex;
    const aspectRatio = animation.width && animation.height ? animation.width / animation.height : 1;

    return (
      <ClickableDiv
        key={animation.animationHashedId}
        ref={isSelected ? selectedRef : undefined}
        className={`${styles.thumbnailContainer} ${isSelected ? styles.thumbnailContainerSelected : ''}`}
        style={{'--thumb-aspect-ratio': String(aspectRatio)} as React.CSSProperties}
        onClick={(): void => {
          onThumbnailClick(offsetIndex);
        }}
      >
        <img src={animation.thumbnailUrl} className={styles.thumbnailImage} alt="" />
        <div className={`${styles.shadow} ${isSelected ? styles.shadowSelected : ''}`} />
        {getPlayIcon(isSelected)}
      </ClickableDiv>
    );
  };

  return (
    <div className={styles.strip}>
      {isCreating ? getCreatingThumbnail() : null}
      {animations.map((animation, index) => getThumbnail(animation, index))}
    </div>
  );
}
